class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this;
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.closeButtons = document.querySelectorAll('[data-cart-drawer-close]');
    this.cartCount = document.querySelector('[data-cart-count]');
    this.cartItems = this.querySelector('#cart-drawer-items');
    this.cartTotal = this.querySelector('#cart-drawer-total');
    this.isCustomsAgreed = false;
    this.isOpen = false;
    
    // Shipping bar elements
    // Currency-based shipping thresholds (in cents)
    this.shippingThresholds = {
      'NZD': 30000,  // $300.00 NZD
      'AUD': 50000,  // $500.00 AUD
    };
    
    // Rest of the world default (in cents)
    this.defaultThreshold = 100000; // $1000.00
    
    this.currentCurrency = 'USD'; // Will be updated from cart data
    this.shippingBarContainer = this.querySelector('#cart-shipping-bar-container');
    this.shippingBarText = this.querySelector('#cart-shipping-bar-text');
    this.remainingAmountElement = this.querySelector('#remaining-amount');
    this.shippingBarInner = this.querySelector('#cart-shipping-bar-inner');
    
    this.init();
  }

  init() {
    // Bind event listeners
    this.bindEvents();
    
    // Initialize cart data on page load
    this.fetchCartData();
    
    // Load gift wrap product
    this.loadGiftWrapProduct();
  }

  // Load gift wrapping product from theme settings
  async loadGiftWrapProduct() {
    try {
      const giftWrapModule = this.querySelector('[data-gift-wrap-module]');
      if (!giftWrapModule) return;
      
      const productHandle = giftWrapModule.getAttribute('data-product-handle');
      if (!productHandle) {
        console.warn('No gift wrap product configured');
        giftWrapModule.style.display = 'none';
        return;
      }
      
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const product = await response.json();
      
      const textElement = this.querySelector('.cart-add-on-module-details-text');
      const imageElement = this.querySelector('.gift-wrap-image');
      const button = this.querySelector('.gift-wrapping-btn');
      
      if (button && product.variants && product.variants.length > 0) {
        button.setAttribute('data-variant-id', product.variants[0].id);
      }
      
      if (textElement && product.price) {
        const formattedPrice = this.formatMoney(product.price);
        textElement.textContent = `Add our signature Meadowlark gift wrapping for ${formattedPrice}`;
      }
      
      if (imageElement && product.featured_image) {
        imageElement.src = product.featured_image;
        imageElement.alt = product.title;

        const media = product.media && product.media[0];
        if (media && media.preview_image) {
          imageElement.width = media.preview_image.width;
          imageElement.height = media.preview_image.height;
        }
      }
      
    } catch (error) {
      console.warn('Gift wrap product not available:', error);
      const giftWrapModule = this.querySelector('[data-gift-wrap-module]');
      if (giftWrapModule) {
        giftWrapModule.style.display = 'none';
      }
    }
  }

  bindEvents() {
    // Use event delegation instead of querying specific elements
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer-toggle]')) {
        e.preventDefault();
        this.open();
      }
      
      // Handle disabled checkout button click - listen on wrapper
      if (e.target.closest('.checkout-btn-wrapper')) {
        const checkoutBtn = e.target.closest('.checkout-btn-wrapper').querySelector('.checkout-btn--disabled');
        if (checkoutBtn) {
          const customsText = document.querySelector('.customs-btn');
          if (customsText) {
            customsText.classList.add('customs-btn--red');
            setTimeout(() => {
              customsText.classList.remove('customs-btn--red');
            }, 400);
          }
        }
      }
    });

    // Close drawer triggers
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.close();
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Handle quantity changes
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity]')) {
        const variantId = e.target.dataset.cartQuantity;
        const quantity = parseInt(e.target.value);
        this.updateCartItem(variantId, quantity);
      }
      
      // Handle customs checkbox change
      if (e.target.matches('#customs-checkbox')) {
        this.isCustomsAgreed = e.target.checked;
        this.updateCheckoutButton();
      }
    });

    // Handle order notes with debouncing
    let noteTimeout;
    this.addEventListener('input', (e) => {
      if (e.target.matches('#cart-note')) {
        clearTimeout(noteTimeout);
        noteTimeout = setTimeout(() => {
          this.updateCartNote(e.target.value);
        }, 500); // Wait 500ms after user stops typing
      }
    });

    // Handle quantity increment/decrement buttons
    this.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-quantity-plus]')) {
        e.preventDefault();
        const variantId = e.target.dataset.cartQuantityPlus;
        const input = this.querySelector(`[data-cart-quantity="${variantId}"]`);
        const currentQuantity = parseInt(input.value);
        this.updateCartItem(variantId, currentQuantity + 1);
      }
      
      if (e.target.matches('[data-cart-quantity-minus]')) {
        e.preventDefault();
        const variantId = e.target.dataset.cartQuantityMinus;
        const input = this.querySelector(`[data-cart-quantity="${variantId}"]`);
        const currentQuantity = parseInt(input.value);
        if (currentQuantity > 1) {
          this.updateCartItem(variantId, currentQuantity - 1);
        } else {
          this.removeCartItem(variantId);
        }
      }
    });

    // Handle item removal
    this.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-remove]')) {
        e.preventDefault();
        const variantId = e.target.dataset.cartRemove;
        this.removeCartItem(variantId);
      }
    });

    // Handle gift wrapping button click
    this.addEventListener('click', (e) => {
      if (e.target.matches('.gift-wrapping-btn') || e.target.closest('.gift-wrapping-btn')) {
        e.preventDefault();
        const button = e.target.matches('.gift-wrapping-btn') ? e.target : e.target.closest('.gift-wrapping-btn');
        this.addGiftWrapToCart(button);
      }
    });

    // Listen for cart updated events from product forms
    document.addEventListener('cart:updated', (e) => {
      this.fetchCartData();
      this.open();
    });

    // Handle cart notes toggle
    this.addEventListener('click', (e) => {
      if (e.target.matches('.cart-notes-label') || e.target.closest('.cart-notes-label')) {
        e.preventDefault();
        const notesContainer = this.querySelector('.cart-notes-container');
        if (notesContainer) {
          notesContainer.classList.toggle('cart-notes-container--open');
        }
      }
    });
  }

  async addGiftWrapToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    
    if (!variantId) {
      console.error('Gift wrap variant ID not found');
      return;
    }

    // Store original content for reset
    const originalContent = button.innerHTML;
    
    try {    
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: 1 }]
        })
      });

      if (!response.ok) throw new Error('Failed to add gift wrapping');
      
      // Refresh cart and show success
      await this.fetchCartData();
      this.showNotification('Gift wrapping added!', 'success');
      
    } catch (error) {
      console.error('Error adding gift wrap:', error);
      this.showNotification('Error adding gift wrapping', 'error');
      
      // Reset button on error
      button.disabled = false;
      button.innerHTML = originalContent;
    }
  }

  toggleCustomsAgreement() {
    this.isCustomsAgreed = !this.isCustomsAgreed;
    this.updateCheckoutButton();
  }

  updateCheckoutButton() {
    const checkoutBtn = this.querySelector('#checkout-btn');
    
    if (checkoutBtn) {
      // Enable checkout if NZD or if customs agreed
      const shouldEnable = this.currentCurrency === 'NZD' || this.isCustomsAgreed;
      checkoutBtn.disabled = !shouldEnable;
      checkoutBtn.classList.toggle('checkout-btn--disabled', !shouldEnable);
    }
  }

  // Public method to open drawer (called from ProductForm)
  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    document.body.classList.remove('mobile-menu-open', 'menu-open', 'search-open');
    document.body.classList.add('cart-drawer-open');
    
    // Focus management for accessibility
    this.focus();
    this.trapFocus();

    // Refresh cart data when opening
    this.fetchCartData();
    
    // Preserve existing customs agreement state instead of always disabling
    setTimeout(() => {
      const checkoutBtn = this.querySelector('#checkout-btn');
      const customsCheckbox = this.querySelector('#customs-checkbox');
      
      if (checkoutBtn && customsCheckbox) {
        // Sync internal state with checkbox state, or auto-enable for NZD
        if (this.currentCurrency === 'NZD') {
          this.isCustomsAgreed = true;
        } else {
          this.isCustomsAgreed = customsCheckbox.checked;
        }
        this.updateCheckoutButton();
      }
    }, 100);
  }

  // Public method to close drawer
  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    document.body.classList.remove('cart-drawer-open');
    
    // Restore focus and scroll
    this.restoreFocus();
  }

  // Public method to render cart contents (called from ProductForm)
  renderContents(cart) {
    this.updateCartUI(cart);
  }

  async addToCart(formData) {
    try {
      // Show loading state
      this.setLoadingState(true);
      
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(Object.fromEntries(formData))
      });

      if (response.ok) {
        const item = await response.json();
        
        // Refresh cart data and open drawer
        await this.fetchCartData();
        this.open();
        
        // Show success message
        this.showNotification('Item added to cart!', 'success');
        
        return item;
      } else {
        const error = await response.json();
        throw new Error(error.description || 'Error adding item to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification(error.message || 'Error adding item to cart', 'error');
      throw error;
    } finally {
      this.setLoadingState(false);
    }
  }

  async updateCartItem(variantId, quantity) {
    try {
      this.setLoadingState(true);
      
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.fetchCartData();
        
        // Publish cart updated event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { variantId, quantity }
        }));
      } else {
        throw new Error('Failed to update cart');
      }
    } catch (error) {
      console.error('Update cart error:', error);
      this.showNotification('Error updating cart', 'error');
    } finally {
      this.setLoadingState(false);
    }
  }

  async removeCartItem(variantId) {
    await this.updateCartItem(variantId, 0);
  }

  async fetchCartData() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();
      
      this.updateCartUI(cart);
      return cart;
    } catch (error) {
      console.error('Fetch cart error:', error);
    }
  }

  updateCartUI(cart) {
    // Update current currency from cart
    this.currentCurrency = cart.currency || 'USD';
    
    // Update cart count (external elements)
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = cart.item_count;
      element.style.display = cart.item_count > 0 ? 'block' : 'none';
    });

    // Update cart total
    if (this.cartTotal) {
      this.cartTotal.textContent = this.formatMoney(cart.total_price);
    }

    // Update shipping bar with currency-specific threshold
    this.updateShippingBar(cart.total_price);

    // Hide/show customs section based on currency
    const customsSection = this.querySelector('.customs-btn');
    if (customsSection) {
      if (this.currentCurrency === 'NZD') {
        customsSection.style.display = 'none';
        this.isCustomsAgreed = true; // Auto-agree for NZD
      } else {
        customsSection.style.display = 'flex';
      }
    }

    // Update Afterpay price (1/4 of total cart price)
    const afterpayPriceElement = document.querySelector('#afterpay-price');
    if (afterpayPriceElement) {
      const afterpayPrice = Math.ceil(cart.total_price / 4); // Round up to nearest cent
      afterpayPriceElement.textContent = this.formatMoney(afterpayPrice);
    }

    // Update order notes
    const cartNoteTextarea = this.querySelector('#cart-note');
    if (cartNoteTextarea && cart.note !== undefined) {
      cartNoteTextarea.value = cart.note || '';
    }

    // Hide/show special instructions section based on cart contents
    const specialInstructionsSection = this.querySelector('.cart-notes-container, .cart-special-instructions, [data-cart-notes]');
    if (specialInstructionsSection) {
      specialInstructionsSection.style.display = cart.items.length > 0 ? 'block' : 'none';
    }

    // Hide/show cart addon footer based on cart contents
    const cartAddonFooter = this.querySelector('.mk-cart-addon-footer');
    if (cartAddonFooter) {
      cartAddonFooter.style.display = cart.items.length > 0 ? 'block' : 'none';
    }

    // Update gift wrapping button state
    this.updateGiftWrapButton(cart.items);

    // Update cart items
    if (this.cartItems) {
      if (cart.items.length === 0) {
        this.cartItems.innerHTML = '<p class="cart-empty">There is nothing in your cart</p>';
      } else {
        this.cartItems.innerHTML = this.renderCartItems(cart.items);
      }
    }

    // Update drawer state based on cart contents
    this.classList.toggle('cart-empty', cart.items.length === 0);

    // Check and update add-on modules state
    this.updateAddOnModulesState(cart.items);
  }

  updateGiftWrapButton(cartItems) {
    const button = this.querySelector('.gift-wrapping-btn');
    if (!button) return;

    const variantId = button.getAttribute('data-variant-id');
    const hasGiftWrap = cartItems.some(item => 
      item.variant_id.toString() === variantId
    );

    // Toggle button state based on cart contents
    button.disabled = hasGiftWrap;
    button.classList.toggle('gift-wrapping-btn--added', hasGiftWrap);
    button.innerHTML = hasGiftWrap 
      ? 'Gift wrapping has been added to your order'
      : 'Add to order';
  }

  updateShippingBar(cartTotal) {
    if (!this.shippingBarContainer || !this.shippingBarText || !this.shippingBarInner) {
      return; // Elements not found, skip update
    }

    // Get threshold for current currency, fallback to default for rest of world
    const threshold = this.shippingThresholds[this.currentCurrency] || this.defaultThreshold;
    
    const remaining = threshold - cartTotal;
    const progress = Math.min((cartTotal / threshold) * 100, 100);

    if (remaining > 0) {
      // Still need more for free shipping
      const remainingFormatted = this.formatMoney(remaining);
      this.shippingBarText.innerHTML = `${remainingFormatted} away from free shipping`;
      this.shippingBarInner.style.width = `${progress}%`;
      this.shippingBarContainer.classList.remove('free-shipping-achieved');
    } else {
      // Free shipping achieved!
      this.shippingBarText.innerHTML = 'You are now eligible for free shipping';
      this.shippingBarInner.style.width = '100%';
      this.shippingBarContainer.classList.add('free-shipping-achieved');
    }
  }

  renderCartItems(items) {
    return items.map(item => {
      // Split product title on pipe character to separate product name and metal
      const titleParts = item.product_title.split(' | ');
      const productName = titleParts[0] || item.product_title;
      const metal = titleParts[1] || '';
      
      // Check if item is gift wrapping (skip labels for these)
      const isAddOn = item.variant_id === 50704309059869;
      
      // Check if item is made to order (check for the _made_to_order property)
      const isMadeToOrder = !isAddOn && item.properties && item.properties._made_to_order === 'true';
      const isReadyToShip = !isAddOn && !isMadeToOrder;

      return `
        <div class="cart-item" data-variant-id="${item.variant_id}">
          <div class="cart-item__image">
            ${item.featured_image?.url ? 
              `<div class="mk-image-wrapper" style="padding-top:${item.featured_image.aspect_ratio ? (1 / item.featured_image.aspect_ratio * 100) : 126.31578947368423}%;">
                <img src="${item.featured_image.url}" 
                     alt="${item.product_title}" 
                     width="${item.featured_image.width}"
                     height="${item.featured_image.height}"
                     loading="lazy">
              </div>` : 
              ''
            }
          </div>
          <div class="cart-item__details">
            <h4 class="cart-item__title">${productName}</h4>
            ${metal ? `<p class="cart-item__metal">${metal}</p>` : ''}
            
            ${item.variant_title && item.variant_title !== 'Default Title' ? 
              item.variant_title.split(' / ').map(variant => 
                `<p class="cart-item__variant">${variant}</p>`
              ).join('') : 
              ''
            }
            
            ${!isAddOn ? `
            <div class="cart-item__quantity">
              <div class="quantity-controls">
                <button 
                  type="button" 
                  class="quantity-btn quantity-minus" 
                  data-cart-quantity-minus="${item.variant_id}"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input 
                  type="number" 
                  value="${item.quantity}" 
                  min="1" 
                  data-cart-quantity="${item.variant_id}"
                  class="cart-item__quantity-input"
                  aria-label="Quantity"
                >
                <button 
                  type="button" 
                  class="quantity-btn quantity-plus" 
                  data-cart-quantity-plus="${item.variant_id}"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>
            ` : ''}
            
            <div class="cart-item__price-container">
              <div class="cart-item__price">${this.formatMoney(item.final_line_price)}</div>
              ${item.original_line_price > item.final_line_price ? 
                `<div class="cart-item__compare-price">${this.formatMoney(item.original_line_price)}</div>` : 
                ''
              }
            </div>
            
            ${isMadeToOrder ? `<p class="cart-item__made-to-order">Made to order: ${item.properties && item.properties._lead_time ? item.properties._lead_time : '4 - 6 weeks'}</p>` : ''}
            ${isReadyToShip ? '<p class="cart-item__ready-to-ship">In stock: Ships this week</p>' : ''}
            
            <button 
              type="button" 
              class="cart-item__remove" 
              data-cart-remove="${item.variant_id}"
              aria-label="Remove ${item.product_title}"
            >
              Remove
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  formatMoney(cents) {
    // Get the money format from theme settings
    const moneyFormat = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}';
    
    // Try to use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    
    // Fallback: manually format using the money format string
    const value = (cents / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  setLoadingState(loading) {
    this.classList.toggle('loading', loading);
  }

  showNotification(message, type = 'info') {
    // Create and show notification
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.focusHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    this.addEventListener('keydown', this.focusHandler);
  }

  restoreFocus() {
    // Return focus to the element that opened the drawer
    const lastFocused = document.querySelector('[data-cart-drawer-toggle]');
    if (lastFocused) {
      lastFocused.focus();
    }
  }

  // Cleanup when element is removed
  disconnectedCallback() {
    if (this.focusHandler) {
      this.removeEventListener('keydown', this.focusHandler);
    }
  }

  updateAddOnModulesState(cartItems) {
    // Check gift wrap module
    const giftWrapModule = this.querySelector('[data-gift-wrap-module]');
    const giftWrapButton = this.querySelector('.gift-wrapping-btn');
    
    if (giftWrapModule && giftWrapButton) {
      const giftWrapVariantId = giftWrapButton.getAttribute('data-variant-id');
      const hasGiftWrap = cartItems.some(item => 
        item.variant_id.toString() === giftWrapVariantId
      );
      
      giftWrapModule.classList.toggle('added', hasGiftWrap);
    }
  }
}

// Define the custom element
customElements.define('cart-drawer', CartDrawer);

// Initialize cart drawer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});